import { useCallback } from "react"
import { generateId, generateSlug } from "../utils"

export function useCategoryOperations(
  categories,
  setCategories,
  relations,
  setRelations,
  currentCategory,
  breadcrumb,
  setBreadcrumb,
  selectedItems,
  setSelectedItems,
  setModalJustClosed,
  notify,
  operations = {}
) {
  const createNewCategory = useCallback(
    (name, color) => {
      const currentChildren = relations
        .filter((r) => r.parentId === currentCategory.id)
        .map((r) => categories.find((c) => c.id === r.childId))
        .filter(Boolean)

      const existing = currentChildren.find(
        (c) => c.name.toLowerCase() === name.toLowerCase()
      )
      if (existing) {
        notify(`"${existing.name}" already exists here`, "warning")
        return false
      }

      const newCategory = { id: generateId(), name, slug: generateSlug(name), color }
      setCategories((prev) => [...prev, newCategory])
      setRelations((prev) => [
        ...prev,
        { parentId: currentCategory.id, childId: newCategory.id },
      ])

      if (operations.onCreate) {
        operations.onCreate(newCategory, currentCategory.id).catch((err) => {
          console.error("Failed to create node:", err)
          notify("Failed to save node", "error")
        })
      }
      return true
    },
    [currentCategory.id, categories, relations, setCategories, setRelations, notify, operations]
  )

  const bulkCreateCategories = useCallback(
    (names) => {
      const currentChildren = relations
        .filter((r) => r.parentId === currentCategory.id)
        .map((r) => categories.find((c) => c.id === r.childId))
        .filter(Boolean)

      const inputDupes = names
        .map((n) => n.name.toLowerCase())
        .filter((n, i, arr) => arr.indexOf(n) !== i)

      if (inputDupes.length > 0) {
        notify(`Duplicate names in input: ${inputDupes.slice(0, 3).join(", ")}`, "warning")
        return false
      }

      const existingDupes = names
        .filter(({ name }) =>
          currentChildren.some((c) => c.name.toLowerCase() === name.toLowerCase())
        )
        .map((n) => n.name)

      if (existingDupes.length > 0) {
        notify(`Already exists here: ${existingDupes.slice(0, 3).join(", ")}`, "warning")
        return false
      }

      const newCategories = names.map(({ name, color }) => ({
        id: generateId(),
        name,
        slug: generateSlug(name),
        color,
      }))

      setCategories((prev) => [...prev, ...newCategories])
      setRelations((prev) => [
        ...prev,
        ...newCategories.map((cat) => ({ parentId: currentCategory.id, childId: cat.id })),
      ])

      if (operations.onBulkCreate) {
        operations.onBulkCreate(newCategories, currentCategory.id).catch((err) => {
          console.error("Failed to bulk create:", err)
          notify("Failed to save nodes", "error")
        })
      }
      return true
    },
    [currentCategory.id, categories, relations, setCategories, setRelations, notify, operations]
  )

  const renameCategory = useCallback(
    (newName, newColor) => {
      if (selectedItems.length !== 1) return
      const categoryId = selectedItems[0]

      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === categoryId
            ? { ...cat, name: newName, slug: generateSlug(newName), color: newColor }
            : cat
        )
      )

      if (categoryId === currentCategory.id) {
        setBreadcrumb((prev) =>
          prev.map((item) => (item.id === categoryId ? { ...item, name: newName } : item))
        )
      }

      if (operations.onRename) {
        operations.onRename(categoryId, newName, newColor).catch((err) => {
          console.error("Failed to rename:", err)
          notify("Failed to save changes", "error")
        })
      }

      setModalJustClosed(true)
    },
    [selectedItems, currentCategory.id, setCategories, setBreadcrumb, setModalJustClosed, notify, operations]
  )

  const wouldCreateCycle = useCallback(
    (parentId, childId, tempRelations = relations) => {
      if (parentId === childId) return true
      const visited = new Set()
      const queue = [parentId]
      while (queue.length > 0) {
        const curr = queue.shift()
        if (curr === childId) return true
        if (visited.has(curr)) continue
        visited.add(curr)
        queue.push(
          ...tempRelations.filter((r) => r.childId === curr).map((r) => r.parentId)
        )
      }
      return false
    },
    [relations]
  )

  const addExistingAsChild = useCallback(
    async (childId) => {
      if (relations.some((r) => r.parentId === currentCategory.id && r.childId === childId)) return
      if (wouldCreateCycle(currentCategory.id, childId)) {
        notify("Cannot add — would create a circular reference", "error")
        return
      }
      if (operations.onAddRelation) {
        try {
          await operations.onAddRelation(currentCategory.id, childId)
        } catch (err) {
          console.error("Failed to add relation:", err)
          notify("Failed to save changes", "error")
        }
      }
    },
    [relations, currentCategory.id, wouldCreateCycle, notify, operations]
  )

  const getCategoryById = useCallback(
    (id) => categories.find((c) => c.id === id),
    [categories]
  )

  const moveSelectedToParent = useCallback(
    (newParentId) => {
      if (selectedItems.length === 0) return

      for (const childId of selectedItems) {
        if (wouldCreateCycle(newParentId, childId)) {
          const cat = getCategoryById(childId)
          notify(`Cannot move "${cat?.name}" — circular reference`, "error")
          return
        }
      }

      const movingCurrent = selectedItems.includes(currentCategory.id)

      setRelations((prev) => {
        let updated = [...prev]
        selectedItems.forEach((childId) => {
          if (newParentId === childId) return
          if (childId === currentCategory.id && breadcrumb.length > 1) {
            const parentOfCurrent = breadcrumb[breadcrumb.length - 2].id
            updated = updated.filter(
              (r) => !(r.parentId === parentOfCurrent && r.childId === childId)
            )
          } else {
            updated = updated.filter(
              (r) => !(r.parentId === currentCategory.id && r.childId === childId)
            )
          }
          if (!updated.some((r) => r.parentId === newParentId && r.childId === childId)) {
            updated.push({ parentId: newParentId, childId })
          }
        })
        return updated
      })

      if (movingCurrent) {
        const newParent = getCategoryById(newParentId)
        if (newParent) {
          const buildPath = (catId, path = []) => {
            const cat = categories.find((c) => c.id === catId)
            if (!cat) return path
            const parentRel = relations.find((r) => r.childId === catId)
            if (parentRel) return buildPath(parentRel.parentId, [{ id: cat.id, name: cat.name }, ...path])
            return [{ id: cat.id, name: cat.name }, ...path]
          }
          setBreadcrumb([
            ...buildPath(newParentId),
            { id: currentCategory.id, name: currentCategory.name },
          ])
        }
      }

      if (operations.onMove) {
        operations.onMove(selectedItems, newParentId).catch((err) => {
          console.error("Failed to move:", err)
          notify("Failed to save changes", "error")
        })
      }

      setSelectedItems([])
    },
    [selectedItems, currentCategory, breadcrumb, categories, relations, setRelations, setBreadcrumb, setSelectedItems, getCategoryById, wouldCreateCycle, notify, operations]
  )

  const deleteSelected = useCallback(
    (navigateTo) => {
      if (selectedItems.length === 0) return
      const deletingCurrent = selectedItems.includes(currentCategory.id)

      setRelations((prev) => {
        let updated = [...prev]
        selectedItems.forEach((childId) => {
          if (childId === currentCategory.id && breadcrumb.length > 1) {
            const parentOfCurrent = breadcrumb[breadcrumb.length - 2].id
            updated = updated.filter(
              (r) => !(r.parentId === parentOfCurrent && r.childId === childId)
            )
          } else {
            updated = updated.filter(
              (r) => !(r.parentId === currentCategory.id && r.childId === childId)
            )
          }
        })
        return updated
      })

      if (operations.onDelete) {
        operations.onDelete(selectedItems).catch((err) => {
          console.error("Failed to delete:", err)
          notify("Failed to delete nodes", "error")
        })
      }

      if (deletingCurrent && breadcrumb.length > 1) {
        navigateTo(breadcrumb.length - 2)
      } else {
        setSelectedItems([])
      }
    },
    [selectedItems, currentCategory, breadcrumb, setRelations, setSelectedItems, notify, operations]
  )

  return {
    createNewCategory,
    bulkCreateCategories,
    renameCategory,
    addExistingAsChild,
    moveSelectedToParent,
    deleteSelected,
    getCategoryById,
    wouldCreateCycle,
  }
}
